import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";
import { useSelector } from "react-redux";
import { Input } from "@progress/kendo-react-inputs";
import { TimePicker } from "@progress/kendo-react-dateinputs";
import { icon, label, number } from "../../config";
import { parseTimeStringToDate } from "../../helper/common";
import UserProfileCard from "../../shared/components/UserProfileCard/UserProfileCard";
import { useProcessedFields } from "../../shared/components/UserProfileCard/userProfileCard.hook";
import { getUserDetailsByUserId } from "../../shared/services/user.service";
import { splitString } from "../../utils";
import { useEditorContext } from "../editor.Context";
import {
  arrayOfObjects,
  convertTo24HourFormat,
  formatDate,
  formatTime,
  generateTimeArray,
  getChipContent,
  getDateAndTime,
  getTimeFormats,
  searchUsers,
  updateChipContent,
} from "../editor.helper";
import { parse, format, isValid } from "date-fns";
import CustomVariableDates from "./TaskCustomVariables/CustomVariableDates";
import CustomVariableDropdown from "./TaskCustomVariables/CustomVariableDropdown";
import CustomVariableInput from "./TaskCustomVariables/CustomVariableInput";
import CustomVariableNumbers from "./TaskCustomVariables/CustomVariableNumbers";

/**
 * Updates User Chip  for Editor
 * PC UpdateChip
 * @author Shivam Mishra
 */
const UserChip = ({ smartChipId, setShowEditChip, assigneeList, setAssigneeList, editorRef }) => {
  const { defaultDetails } = useSelector((state) => state.tasks);
  const { recentAssignmentList } = useSelector((state) => state.auth);

  const editorState = useEditorContext();
  const { selectedIndex, setSelectedIndex, containerRef } = editorState;

  /**
   * Function to handle update events on user chip.
   * @param {Object} assignee - The assignee object.
   *@author Shivam Mishra
   */
  const handleClick = (assignee) => {
    if (!editorRef?.current) return;
    const idParts = smartChipId.split("_user_");
    let newId = "";
    if (idParts.length === 2) {
      newId = `${idParts[0]}_user_${assignee.value}`;
    } else {
      newId = smartChipId; 
    }
    updateChipContent(editorRef, smartChipId, assignee.label, { id: newId });
    setShowEditChip(false);
  };

  /**
   * Function to update assignee list .
   * @param {e} event.
   */
  const updateUserList = (e) => {
    setAssigneeList(searchUsers(e.target.value, defaultDetails, assigneeList));
  };

  useEffect(() => {
    setSelectedIndex(number.ZERO);
  }, [assigneeList]);

  /**
   * Function to handle update events on user chip.
   * @param {Object} assignee - The assignee object.
   *@author Shivam Mishra
   */
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && assigneeList.length) {
      e.preventDefault();
      handleClick(assigneeList[selectedIndex]);
    } else if (e.key === "ArrowUp") {
      setSelectedIndex((prevIndex) => Math.max(number.ZERO, prevIndex - number.ONE));
    } else if (e.key === "ArrowDown") {
      setSelectedIndex((prevIndex) => Math.min(assigneeList.length - number.ONE, prevIndex + number.ONE));
    }
  };

  return (
    <div className='dt-editor-menu'>
      <div className='w-100' id={`update-editor-user-chip`}>
        <div className='d-flex justify-content-between p-3'>
          <div className='font-weight-bold'>{label.USER}</div>
          <div className='small'>{label.ESC_PRESS}</div>
        </div>
        <div className='ml-3 mr-3'>
          <Input onChange={updateUserList} autoFocus={true} onKeyDown={handleKeyDown} />
        </div>
        <div ref={containerRef} className='dt-editor-users overflow-auto'>
          {assigneeList?.map((assignee, index) => {
            if (assignee?.isSubHeading) {
              return (
                <>
                  {recentAssignmentList?.find((recentItem) => recentItem?.AssignedUserId) ? (
                    <span className='dropdown-subheading'>{assignee?.label}</span>
                  ) : null}
                </>
              );
            }
            return (
              <div
                key={index}
                onClick={() => handleClick(assignee)}
                className={`dt-editor-users-list cursor-pointer d-flex ${index === selectedIndex ? "user-selected" : ""}`}
                id={`update-user-chip-${index}`}>
                <div className='mr-2'>
                  {assignee.PhotoLinks?.small ? (
                    <img src={assignee.PhotoLinks?.small} className='userlist-dp rounded-circle' />
                  ) : (
                    <div className='userlist-icon'>{icon.ACCOUNT_CIRCLE}</div>
                  )}
                </div>
                <div>
                  <div className='assignee-info d-block'>{assignee.label}</div>
                  <div className='small info-text'>{assignee.Email}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

/**
 * Updates Date Chip  for Editor
 * PC UpdateChip
 * @author Shivam Mishra
 */

const DateChip = ({ smartChipId, setShowEditChip, editorRef }) => {
  const initialDateText = getChipContent(editorRef, smartChipId) || "";
  let dateObject;
  try {
    dateObject = getDateAndTime(initialDateText);
  } catch (e) {
    dateObject = new Date();
  }
  const [selected, setSelected] = useState(new Date(dateObject));
  const [editableDate, setEditableDate] = useState(formatDate(dateObject));
  const [showCalendar, setShowCalendar] = useState(false);

  /**
   * Function to handle update on date chip.
   *@author Shivam Mishra
   */
  const handleDateUpdate = (date) => {
    if (date) {
      setSelected(date);
      const formattedDate = formatDate(date);
      setEditableDate(formattedDate);
      updateChipContent(editorRef, smartChipId, formattedDate);
      setShowEditChip(false);
    }
  };

  /**
   * Function to handle delete date chip
   */
  const handleDelete = () => {
    if (editorRef?.current && smartChipId) {
      const { view } = editorRef.current.state;
      const { state, dispatch } = view;
      const { doc, tr } = state;
      doc.descendants((node, pos) => {
        if (node.attrs?.id === smartChipId) {
          dispatch(tr.delete(pos, pos + node.nodeSize));
          return false;
        }
        return true;
      });
      setShowEditChip(false);
    }
  };

  return (
    <div className='position-relative date-chip-container' tabIndex='0'>
      <div className='p-2'>
        <h6>{label.SELECTED_DATE}</h6>
        <div className='d-flex align-items-center'>
          <Input
            value={editableDate}
            onChange={(e) => setEditableDate(e.target.value)}
            onFocus={() => setShowCalendar(true)}
            className='flex-grow-1'
          />
          <div className='square-icon square-icon-extra-small ml-1' onClick={handleDelete} title='Delete'>
            {icon.DELETE_ICON}
          </div>
        </div>
        {showCalendar && (
          <div className='zoom w-100'>
            <DayPicker
              mode='single'
              selected={selected}
              onSelect={handleDateUpdate}
              id='update-chip-date-picker'
            />
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Updates Date Chip  for Editor
 * PC UpdateChip
 * @author Shivam Mishra
 */
const DropDownChip = ({ smartChipId, setShowEditChip, smartChipIds, editorRef }) => {
  const matchingObject = useMemo(() => {
    return arrayOfObjects?.find((obj) => obj.id === parseInt(smartChipIds))?.value;
  }, [smartChipIds]);

  /**
   * Function to handle update on dropdown chip.
   * @param {String} name - The dd name
   * @author Shivam Mishra
   */
  const handleClick = (name) => {
    if (updateChipContent(editorRef, smartChipId, name)) {
      setShowEditChip(false);
    }
  };

  return (
    <div className='dt-editor-users overflow-auto w-100'>
      {matchingObject?.map((name, index) => (
        <div className='dt-editor-users-item cursor-pointer word-break-all' key={index} id={`dd-options-${index}`} onClick={() => handleClick(name)}>
          {name}
        </div>
      ))}
    </div>
  );
};

const TimeChip = ({ smartChipId, setShowEditChip, editorRef }) => {
  const initialTimeText = getChipContent(editorRef, smartChipId) || "";
  
  /**
   * Validates if hours and minutes are within valid 24-hour time range
   * @author Muskan Thakur
   */
  const isValidTimeRange = (hoursNum, minutesNum) => {
    return hoursNum >= number.ZERO && hoursNum <= number.TWENTY_THREE && minutesNum >= number.ZERO && minutesNum <= number.FIFTY_NINE;
  };
  
  /**
   * Function to validate and convert time to 24-hour format (used for initial load)
   */
  const loadAndConvertTime = (timeString) => {
    if (!timeString || !timeString.trim()) {
      return "00:00";
    }

    const trimmedTime = timeString.trim();
    
    const time24Pattern = /^([0-1]?[0-9]|2[0-3]):([0-5][0-9])$/;
    if (time24Pattern.test(trimmedTime)) {
      const [hours, minutes] = trimmedTime.split(":");
      const hoursNum = parseInt(hours, number.TEN);
      const minutesNum = parseInt(minutes, number.TEN);
      if (isValidTimeRange(hoursNum, minutesNum)) {
        return `${hoursNum.toString().padStart(2, "0")}:${minutesNum.toString().padStart(2, "0")}`;
      }
    }

    let normalizedTime = trimmedTime.replace(/\s+/g, " ").trim();
    const formats = getTimeFormats(normalizedTime);

    for (const { pattern, time } of formats) {
      try {
        const parsedDate = parse(time, pattern, new Date());
        if (isValid(parsedDate)) {
          return format(parsedDate, "HH:mm");
        }
      } catch (e) {
        
      }
    }

    return convertTo24HourFormat(trimmedTime);
  };

  const initialTime24 = initialTimeText ? loadAndConvertTime(initialTimeText) : "00:00";
  
  /**
   * Convert time string to Date object for TimePicker
   */
  const getInitialTimeDate = () => {
    try {
      return parseTimeStringToDate(initialTime24);
    } catch (e) {
      return parseTimeStringToDate("00:00");
    }
  };

  const [selectedTimeDate, setSelectedTimeDate] = useState(getInitialTimeDate());

  /**
   * Function to handle TimePicker change
   * @author Muskan Thakur
   */
  const handleTimePickerChange = (event) => {
    const selectedDate = event?.value || event?.target?.value;
    if (selectedDate) {
      const hours = selectedDate.getHours().toString().padStart(2, "0");
      const minutes = selectedDate.getMinutes().toString().padStart(2, "0");
      const time24Hour = `${hours}:${minutes}`;
      const time12Hour = formatTime(time24Hour);
      
      setSelectedTimeDate(selectedDate);
      updateChipContent(editorRef, smartChipId, time12Hour);
    }
  };

  /**
   * Function to handle delete time chip
   */
  const handleDelete = () => {
    if (editorRef?.current && smartChipId) {
      const { view } = editorRef.current.state;
      const { state, dispatch } = view;
      const { doc, tr } = state;
      doc.descendants((node, pos) => {
        if (node.attrs?.id === smartChipId) {
          dispatch(tr.delete(pos, pos + node.nodeSize));
          return false;
        }
        return true;
      });
      setShowEditChip(false);
    }
  };

  return (
    <div className='p-2 overflow-hidden position-relative'>
      <h6 className='mb-2'>{label.SELECTED_TIME}</h6>
      <div className='d-flex align-items-center'>
        <div className='flex-grow-1'>
          <TimePicker
            size='small'
            value={selectedTimeDate}
            onChange={handleTimePickerChange}
            steps={{ minute: 1 }}
          />
        </div>
        <div className='square-icon square-icon-extra-small ml-1' onClick={handleDelete} title='Delete'>
          {icon.DELETE_ICON}
        </div>
      </div>
    </div>
  );
};
const handleChangeUser = (assigneeList) => {
  return <UserChip />;
};

/**
 * Wrapper component that handles UserProfileCard -> UserChip transition
 * Shows UserProfileCard first, then UserChip when action is clicked
 * @author Sarthak Arora
 */
const UserProfileChipWrapper = ({ smartChipId, setShowEditChip, assigneeList, setAssigneeList, editorRef }) => {
  const [showUserChip, setShowUserChip] = useState(false);
  const { defaultDetails } = useSelector((state) => state.tasks);
  const { user } = useSelector((state) => state.auth);
  const [userDetails, setUserDetails] = useState({});
  const [smartChip, userId, timestamp, smartChipType, smartChipIds] = splitString(smartChipId, "_") ?? [];

  /**
   * Fetches user details from the API
   * @author Sarthak Arora
   */
  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const response = await getUserDetailsByUserId({ uId: smartChipIds, companyId: user?.companyId });
        if (response.success) {
          let user = response.data;
          user.UserDetails.PhotoLinks = user?.UserDetails?.PhotoLinks?.small;
          setUserDetails(user);
          const userIdValue = user.UserDetails?.UserId;
          if (!userIdValue) {
            setShowUserChip(true);
          }
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
      }
    };

    if (userId && user?.companyId) {
      fetchUserDetails();
    }
  }, [userId, user?.companyId]);

  const processedFields = useProcessedFields(userDetails);

  /**
   * Handles the action click event
   * @author Sarthak Arora
   */
  const handleActionClick = useCallback(() => {
    setShowUserChip(true);
  }, []);

  if (showUserChip) {
    return (
      <UserChip
        smartChipId={smartChipId}
        setShowEditChip={setShowEditChip}
        assigneeList={assigneeList}
        setAssigneeList={setAssigneeList}
        editorRef={editorRef}
      />
    );
  }

  return (
    <UserProfileCard user={userDetails} fields={processedFields} onActionClick={handleActionClick} actionIcon={icon.USER_PEN} type={"Description"} />
  );
};

// Define a mapping from strings to components
const componentMap = {
  user: UserProfileChipWrapper,
  date: DateChip,
  time: TimeChip,
  dd: DropDownChip,
  customDropdown: CustomVariableDropdown,
  customInput: CustomVariableInput,
  customDate: CustomVariableDates,
  customNumber: CustomVariableNumbers,
};

/**
 * Updates Chip  for Editor
 * PC KendoEditor
 * @author Shivam Mishra
 */
const UpdateChip = ({ smartChipId, setShowEditChip, assigneeList, setAssigneeList, editorRef }) => {
  const [smartChip, userId, timestamp, smartChipType, smartChipIds] = splitString(smartChipId, "_") ?? [];

  const RenderedComponent = componentMap[smartChipType] || null;
  const isUserChip = smartChipType === "user";

  return (
    <div>
      {RenderedComponent && (
        <RenderedComponent
          smartChipId={smartChipId}
          setShowEditChip={setShowEditChip}
          smartChipIds={smartChipIds}
          assigneeList={assigneeList}
          setAssigneeList={setAssigneeList}
          editorRef={editorRef}
        />
      )}
    </div>
  );
};

export default React.memo(UpdateChip);
